import { Anchor, AnchorExtendedProps, Button, ResponsiveContext, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import React, { useContext } from 'react';
import { LinkProps, useLocation, useNavigate } from 'react-router-dom';

/** Whether the user is on the page a nav link goes to (or one below it) */
export const useIsActivePath = (to: LinkProps['to']) => {
  const { pathname } = useLocation();
  if (typeof to !== 'string') return false;
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
};

/**
 * A header nav link. Like the project menus, the page the user is on is underlined in the bar and
 * the others dimmed; in the bar it's as tall as the bar, so the underline sits on its bottom edge.
 */
export const AnchorLink: React.FC<AnchorLinkProps> = ({ to, style, icon, ...props }) => {
  const navigate = useNavigate();
  const theme = useContext(ThemeContext);
  const active = useIsActivePath(to);
  const inBar = useContext(ResponsiveContext) !== 'small';

  return (
    <Anchor
      as={Button}
      aria-current={active ? 'page' : undefined}
      {...props}
      // Icons only in the phone drawer; the bar needs the room
      icon={inBar ? undefined : icon}
      style={{
        whiteSpace: 'nowrap',
        ...(inBar ? { height: '72px', display: 'flex', alignItems: 'center' } : {}),
        ...(active
          ? { boxShadow: `inset 0 -3px 0 ${normalizeColor(inBar ? 'accent-1' : 'text', theme)}` }
          : { opacity: 0.85 }),
        ...style,
      }}
      onClick={() => navigate(to)}
    />
  );
};

export type AnchorLinkProps = LinkProps & AnchorExtendedProps;
